import { useEffect, useState } from 'react';
import api from '../api';
import { auth } from '../auth';
import { getTeamDisplayName } from '../teamDisplay';

const emptyForm = {
  coachName: '',
  email: '',
  phone: '',
  password: '',
};

const MAX_SOURCE_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_UPLOAD_IMAGE_CHARS = 320000;
const MAX_IMAGE_DIMENSION = 512;

function getTeamImageUrl(user = {}) {
  return user?.teamMascotUrl || user?.teamLogoUrl || user?.mascotUrl || user?.logoUrl || '';
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read that image.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load that image.'));
    image.src = dataUrl;
  });
}

function canvasToDataUrl(canvas, type, quality) {
  return canvas.toDataURL(type, quality);
}

async function prepareBrandingImage(file) {
  const dataUrl = await readFileAsDataUrl(file);
  if (dataUrl.length <= MAX_UPLOAD_IMAGE_CHARS) return dataUrl;

  const image = await loadImage(dataUrl);
  const ratio = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * ratio));
  const height = Math.max(1, Math.round(image.height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const qualities = [0.88, 0.78, 0.68, 0.58];
  for (const quality of qualities) {
    const compressed = canvasToDataUrl(canvas, 'image/jpeg', quality);
    if (compressed.length <= MAX_UPLOAD_IMAGE_CHARS) return compressed;
  }

  const smallest = canvasToDataUrl(canvas, 'image/jpeg', 0.5);
  if (smallest.length > MAX_UPLOAD_IMAGE_CHARS) {
    throw new Error('Prepared image is too large.');
  }

  return smallest;
}

export default function TeamCoaches({ user, onTokenChange }) {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [removingCoachId, setRemovingCoachId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [teamImageUrl, setTeamImageUrl] = useState(getTeamImageUrl(user));
  const teamDisplayName = getTeamDisplayName(user);

  useEffect(() => {
    let cancelled = false;

    async function loadCoaches() {
      try {
        const { data } = await api.get('/api/team/coaches');
        if (!cancelled) {
          setCoaches(data.coaches || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Could not load coaches.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCoaches();

    return () => {
      cancelled = true;
    };
  }, []);

  function handleChange(e) {
    setForm((current) => ({ ...current, [e.target.name]: e.target.value }));
  }

  function saveBrandingImage(imageDataUrl) {
    setBrandingSaving(true);
    setError('');
    setMessage('');

    return api
      .patch('/api/team/profile/branding', { teamMascotUrl: imageDataUrl })
      .then(({ data }) => {
        setTeamImageUrl(data.teamMascotUrl || '');
        if (data.token) {
          auth.setToken(data.token);
          onTokenChange?.(data.token);
        }
        setMessage(imageDataUrl ? 'Team logo saved.' : 'Team logo removed.');
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Could not update team logo.');
      })
      .finally(() => {
        setBrandingSaving(false);
      });
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    setError('');
    setMessage('');

    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose a team logo or mascot image.');
      return;
    }
    if (file.size > MAX_SOURCE_IMAGE_BYTES) {
      setError('Please choose an image under 5 MB.');
      return;
    }

    prepareBrandingImage(file)
      .then((imageDataUrl) => saveBrandingImage(imageDataUrl))
      .catch(() => {
        setError('Could not prepare that image. Please try a smaller PNG or JPG.');
      });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const { data } = await api.post('/api/team/coaches', form);
      setCoaches((current) => [...current, data.coach]);
      setForm(emptyForm);
      setMessage('Coach added to the team.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add coach.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveCoach(coach) {
    if (!coach?.userID || coach.isPrimaryCoach) return;

    const label = coach.coachName || coach.email || 'this coach';
    const confirmed = window.confirm(`Remove ${label} from this team account?`);
    if (!confirmed) return;

    setRemovingCoachId(coach.userID);
    setError('');
    setMessage('');

    try {
      await api.delete(`/api/team/coaches/${encodeURIComponent(coach.userID)}`);
      setCoaches((current) => current.filter((item) => item.userID !== coach.userID));
      setMessage(`${label} was removed from this team account.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove coach.');
    } finally {
      setRemovingCoachId('');
    }
  }

  if (!user?.isPrimaryCoach) {
    return (
      <section className="surface-card">
        <p className="eyebrow">Manage Coaches</p>
        <h2 className="mt-3 text-4xl font-semibold text-brand-black">Primary coach access only</h2>
        <p className="mt-3 max-w-3xl text-lg leading-8 text-stone-700">
          Only the primary coach on this team can add other coaches to the account.
        </p>
      </section>
    );
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[1.05fr_0.95fr]">
      <section className="surface-card">
        <p className="eyebrow">Team Account</p>
        <h2 className="mt-3 text-4xl font-semibold text-brand-black">Manage coaches</h2>
        <p className="mt-3 text-lg leading-8 text-stone-700">
          One primary coach manages the account and can add other coaches below.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg bg-brand-offwhite px-4 py-4">
            <p className="form-label">School</p>
            <p className="mt-2 text-lg font-semibold text-brand-black">{user?.schoolName || 'Not listed'}</p>
          </div>
          <div className="rounded-lg bg-brand-offwhite px-4 py-4">
            <p className="form-label">Team</p>
            <p className="mt-2 text-lg font-semibold text-brand-black">{teamDisplayName}</p>
          </div>
        </div>

        <div className="mt-6 rounded-lg border-2 border-stone-200 bg-brand-offwhite px-5 py-5">
          <p className="eyebrow">Team Logo</p>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex h-28 w-28 items-center justify-center rounded-lg border-2 border-stone-200 bg-white p-3">
              {teamImageUrl ? (
                <img src={teamImageUrl} alt={`${teamDisplayName} logo`} className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-center text-base font-semibold text-stone-500">{teamDisplayName}</span>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-semibold text-brand-black">Personalize this team account</h3>
              <p className="mt-2 text-base leading-7 text-stone-700">
                Add a mascot or logo so coaches can quickly confirm they are working in the right team account.
                The same image will show in the phone and tablet app.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <label className="btn-primary cursor-pointer text-base">
                  {brandingSaving ? 'Saving...' : 'Upload mascot or logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="sr-only"
                    onChange={handleImageChange}
                    disabled={brandingSaving}
                  />
                </label>
                {teamImageUrl ? (
                  <button
                    type="button"
                    className="rounded-lg border border-stone-300 px-4 py-3 text-base font-semibold text-stone-700 transition hover:bg-white"
                    onClick={() => saveBrandingImage('')}
                    disabled={brandingSaving}
                  >
                    Remove image
                  </button>
                ) : null}
              </div>
              <p className="mt-3 text-sm text-stone-500">
                Use a PNG, JPG, GIF, or WEBP. Mick OC will resize it for the app.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <p className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-700">
            {error}
          </p>
        )}

        {message && (
          <p className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-base text-emerald-700">
            {message}
          </p>
        )}

        <div className="mt-6 space-y-4">
          {loading && <div className="rounded-lg bg-brand-offwhite px-4 py-5 text-base text-stone-700">Loading coaches...</div>}

          {!loading && coaches.length === 0 && (
            <div className="rounded-lg bg-brand-offwhite px-4 py-5 text-base text-stone-700">
              No coaches added yet.
            </div>
          )}

          {!loading &&
            coaches.map((coach) => (
              <article key={coach.userID} className="rounded-lg border-2 border-stone-200 bg-brand-offwhite px-5 py-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-2xl font-semibold text-brand-black">{coach.coachName || coach.email}</h3>
                    <p className="mt-2 text-base text-stone-700">{coach.email}</p>
                    {coach.phone ? <p className="mt-1 text-sm text-stone-500">{coach.phone}</p> : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-stone-700">
                      {coach.isPrimaryCoach ? 'Primary coach' : 'Coach'}
                    </span>
                    {!coach.isPrimaryCoach ? (
                      <button
                        type="button"
                        className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => handleRemoveCoach(coach)}
                        disabled={removingCoachId === coach.userID}
                      >
                        {removingCoachId === coach.userID ? 'Removing...' : 'Remove coach'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
        </div>
      </section>

      <section className="surface-card">
        <p className="eyebrow">Add Coach</p>
        <h2 className="mt-3 text-4xl font-semibold text-brand-black">Create another coach login</h2>
        <p className="mt-3 text-lg leading-8 text-stone-700">
          This adds another coach to the same team account. They will sign in with the email and password you set here.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div>
            <label htmlFor="coachName" className="mb-2 block text-base font-medium text-stone-700">
              Coach name
            </label>
            <input
              id="coachName"
              name="coachName"
              value={form.coachName}
              onChange={handleChange}
              placeholder="Coach Smith"
              className="input-field text-lg"
              required
            />
          </div>

          <div>
            <label htmlFor="coach-email" className="mb-2 block text-base font-medium text-stone-700">
              Email
            </label>
            <input
              id="coach-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="coach@school.edu"
              className="input-field text-lg"
              required
            />
          </div>

          <div>
            <label htmlFor="coach-phone" className="mb-2 block text-base font-medium text-stone-700">
              Phone
            </label>
            <input
              id="coach-phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="555-555-5555"
              className="input-field text-lg"
            />
          </div>

          <div>
            <label htmlFor="coach-password" className="mb-2 block text-base font-medium text-stone-700">
              Temporary password
            </label>
            <input
              id="coach-password"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Temporary password"
              className="input-field text-lg"
              required
            />
          </div>

          <button type="submit" disabled={saving} className="btn-primary w-full justify-center text-base">
            {saving ? 'Adding coach...' : 'Add coach'}
          </button>
        </form>
      </section>
    </div>
  );
}
